import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

const OPTIONS = [
  {
    name: "cyan",
    label: "Neon Cyan",
    accent: "#16cfe0",
    glow: "rgba(22,207,224,0.36)",
  },
  {
    name: "purple",
    label: "Purple",
    accent: "#9b6ddb",
    glow: "rgba(155,109,219,0.37)",
  },
  {
    name: "emerald",
    label: "Emerald",
    accent: "#36b86b",
    glow: "rgba(54,184,107,0.36)",
  },
  {
    name: "amber",
    label: "Amber",
    accent: "#c89232",
    glow: "rgba(200,146,50,0.35)",
  },
  {
    name: "lava",
    label: "Crimson Red",
    accent: "#c84a52",
    glow: "rgba(200,74,82,0.35)",
  },
  {
    name: "midnight",
    label: "Midnight",
    accent: "#527fbd",
    glow: "rgba(82,127,189,0.35)",
  },
  {
    name: "aurora",
    label: "Aurora",
    accent: "#43b9ad",
    glow: "rgba(67,185,173,0.35)",
  },
] as const;

function getStoredUser(): any {
  try {
    const raw =
      localStorage.getItem("auth_user") ||
      localStorage.getItem("ava_user") ||
      localStorage.getItem("user") ||
      sessionStorage.getItem("auth_user") ||
      sessionStorage.getItem("ava_user") ||
      sessionStorage.getItem("user");

    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function getUserThemeKey() {
  try {
    let email =
      localStorage.getItem("email") ||
      sessionStorage.getItem("email") ||
      "";

    if (!email) {
      const raw =
        localStorage.getItem("auth_user") ||
        sessionStorage.getItem("auth_user") ||
        localStorage.getItem("user") ||
        sessionStorage.getItem("user") ||
        "";

      if (raw) {
        const parsed = JSON.parse(raw);
        email = String(
          parsed?.email ||
          parsed?.user?.email ||
          ""
        ).toLowerCase();
      }
    }

    return email
      ? `ava_theme:${email.toLowerCase()}`
      : "ava_theme";
  } catch {
    return "ava_theme";
  }
}

function getPlanState() {
  const user = getStoredUser();
  const role = String(
    user?.role ||
      localStorage.getItem("ava_user_role") ||
      localStorage.getItem("role") ||
      ""
  ).toLowerCase();

  const sub = String(
    user?.subscription_status ||
      localStorage.getItem("subscription_status") ||
      ""
  ).toLowerCase();

  const isAdmin = ["admin", "owner", "superadmin"].includes(role);
  const isPro = role === "vip" || sub === "active";

  return { isAdmin, isPro };
}

const Theme: React.FC = () => {
  const nav = useNavigate();
  const { theme, setTheme, themeColors } = useContext(ThemeContext);
  const { isAdmin, isPro } = getPlanState();
  const canCustomize = isAdmin || isPro;

  // Theme Studio must receive clicks. Disable pointer capture from visual/canvas layers while on this page.
  useEffect(() => {
    const touched: Array<{ el: HTMLElement; prev: string }> = [];

    const selectors = [
      "canvas",
      "[class*='ava']",
      "[class*='Ava']",
      "[id*='ava']",
      "[id*='Ava']",
      "[class*='orb']",
      "[class*='Orb']"
    ];

    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((node) => {
        const el = node as HTMLElement;
        if (!el || el.closest("[data-theme-studio-root]")) return;

        const style = window.getComputedStyle(el);
        const pos = style.position;
        const z = Number(style.zIndex || "0");

        if (node.tagName.toLowerCase() === "canvas" || pos === "fixed" || pos === "absolute" || z > 10) {
          touched.push({ el, prev: el.style.pointerEvents });
          el.style.pointerEvents = "none";
        }
      });
    });

    return () => {
      touched.forEach(({ el, prev }) => {
        el.style.pointerEvents = prev;
      });
    };
  }, []);

  const applyTheme = (color: string) => {
    if (!canCustomize) {
      alert("Theme customization is available for Pro users. Free accounts stay in Ava’s low-power dimmed mode.");
      return;
    }

    try {
      setTheme(color as any);
    } catch (e) {
      console.error("Theme apply failed:", e);
      alert("Theme could not be applied.");
    }
  };

  return (
    <div data-theme-studio-root="true" style={{
      position: "relative",
      zIndex: 2147483647,
      isolation: "isolate",
      pointerEvents: "auto",
      minHeight: "100vh",
      padding: "26px",
      fontFamily: "Orbitron, sans-serif",
      background: themeColors.bg,
      color: themeColors.text,
    }}>
      <section style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 50, pointerEvents: "auto" }}>
        <div style={{
          borderRadius: 28,
          padding: "24px",
          marginBottom: 26,
          background: themeColors.panel,
          border: `1px solid ${themeColors.border}`,
          boxShadow: themeColors.glow,
        }}>
          <div style={{
            fontSize: 12,
            letterSpacing: "0.24em",
            color: themeColors.accent,
            fontWeight: 900,
            textTransform: "uppercase",
            marginBottom: 8,
          }}>
            Ava Personalization
          </div>

          <h1 style={{
            margin: 0,
            fontSize: "clamp(34px, 6vw, 64px)",
            color: themeColors.accent,
            textShadow: themeColors.glow,
          }}>
            Theme Studio
          </h1>

          <p style={{ opacity: 0.82, maxWidth: 780, lineHeight: 1.6 }}>
            Choose Ava’s color energy. Pro theme changes apply instantly and stay saved for your account experience.
          </p>

          {!canCustomize && (
            <div style={{
              marginTop: 14,
              padding: "12px 14px",
              borderRadius: 14,
              border: "1px solid rgba(255,190,80,0.35)",
              background: "rgba(255,190,80,0.08)",
              color: "#ffd27a",
              fontSize: 13,
              fontWeight: 800,
            }}>
              Free account detected: Theme customization is locked on the Free plan. Upgrade to unlock Ava themes and personalization.
            </div>
          )}
        </div>

        <div style={{
          position: "relative",
          zIndex: 2147483647,
          pointerEvents: "auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 22,
          alignItems: "center",
        }}>
          {OPTIONS.map((option) => {
            const active = option.name === theme;

            return (
              <button
                key={option.name}
                type="button"
                data-ava-theme-card={option.name}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  applyTheme(option.name);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  applyTheme(option.name);
                }}
                style={{
                  minHeight: 190,
                  borderRadius: 28,
                  padding: 18,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  position: "relative",
                  zIndex: 100,
                  opacity: canCustomize ? 1 : 0.55,
                  background: active
                    ? `radial-gradient(circle at center, ${option.glow}, rgba(0,0,0,0.55) 58%)`
                    : "rgba(5,10,18,0.82)",
                  border: active
                    ? `2px solid ${option.accent}`
                    : `1px solid ${themeColors.border}`,
                  boxShadow: active
                    ? `0 0 34px ${option.glow}`
                    : "0 12px 28px rgba(0,0,0,0.32)",
                  color: themeColors.text,
                  transition: "transform 0.16s ease, box-shadow 0.16s ease, border 0.16s ease",
                }}
              >
                <div style={{
                  width: 92,
                  height: 92,
                  borderRadius: "999px",
                  margin: "0 auto 16px",
                  background: `radial-gradient(circle at 35% 30%, #ffffff, ${option.accent} 34%, #02040a 78%)`,
                  border: `2px solid ${option.accent}`,
                  boxShadow: `0 0 26px ${option.glow}`,
                }} />

                <div style={{
                  fontSize: 15,
                  fontWeight: 900,
                  textAlign: "center",
                  letterSpacing: "0.04em",
                }}>
                  {option.label}
                </div>

                <div style={{
                  marginTop: 8,
                  fontSize: 11,
                  textAlign: "center",
                  color: active ? option.accent : "rgba(236,250,255,0.62)",
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                }}>
                  {active ? "ACTIVE" : canCustomize ? "SELECT" : "LOCKED"}
                </div>
              </button>
            );
          })}
        </div>

        
      
</section>
    </div>
  );
};

export default Theme;
